import { scaleLinear, scaleTime } from 'd3-scale';
import { Group } from '@visx/group';
import { LinePath } from '@visx/shape';
import dayjs from 'dayjs';
import { formatSeriesData } from './Charts/MultiPredictionTimeSeriesChart/utils';
import { memo } from 'react';
import styled from 'styled-components';

const STROKE_WIDTH = 2;
const HALF_STROKE_WIDTH = STROKE_WIDTH / 2;

const NoDataMessage = styled.p`
    color: ${({ theme }) => theme.neutral_5};
    text-align: center;
`;

const GoogleTikTokSparkline = memo(
    ({
        monthlyGoogleTimeSeries,
        weeklyGoogleTimeSeries,
        weeklyTikTokTimeSeries,
        width,
        height,
        margin = {
            t: HALF_STROKE_WIDTH,
            b: HALF_STROKE_WIDTH,
            l: HALF_STROKE_WIDTH,
            r: HALF_STROKE_WIDTH,
        },
        strokeWidth = STROKE_WIDTH,
    }) => {
        let googleTimeSeries =
            weeklyTikTokTimeSeries?.length > 52 ||
            !weeklyGoogleTimeSeries ||
            !weeklyTikTokTimeSeries
                ? monthlyGoogleTimeSeries
                : weeklyGoogleTimeSeries;
        googleTimeSeries = googleTimeSeries?.length ? googleTimeSeries : null;
        if (!(googleTimeSeries || weeklyTikTokTimeSeries))
            return <NoDataMessage>No data</NoDataMessage>;
        const dataGoogle = formatSeriesData(
            [{ timeSeries: googleTimeSeries }].filter(
                ({ timeSeries }) => timeSeries,
            ),
        );
        const dataTikTok = formatSeriesData(
            [{ timeSeries: weeklyTikTokTimeSeries }].filter(
                ({ timeSeries }) => timeSeries,
            ),
        );
        const xMin =
            googleTimeSeries && weeklyTikTokTimeSeries
                ? Math.max(dataGoogle.xMin, dataTikTok.xMin)
                : googleTimeSeries
                  ? dataGoogle.xMin
                  : dataTikTok.xMin;
        const xMax =
            googleTimeSeries && weeklyTikTokTimeSeries
                ? Math.min(dataGoogle.xMax, dataTikTok.xMax)
                : googleTimeSeries
                  ? dataGoogle.xMax
                  : dataTikTok.xMax;
        const xScale = scaleTime()
            .domain([xMin, xMax])
            .range([STROKE_WIDTH, width - STROKE_WIDTH]);
        const yScaleSearches = scaleLinear()
            .domain([dataGoogle.yMin, dataGoogle.yMax])
            .range([height - STROKE_WIDTH, STROKE_WIDTH]);
        const yScaleViews = scaleLinear()
            .domain([dataTikTok.yMin, dataTikTok.yMax])
            .range([height - STROKE_WIDTH, STROKE_WIDTH]);
        const xGoogle = (d) =>
            xScale(dayjs(dataGoogle.accessors.xAccessor(d)).toDate());
        const yGoogle = (d) =>
            yScaleSearches(dataGoogle.accessors.yAccessor(d));
        const xTikTok = (d) =>
            xScale(dayjs(dataTikTok.accessors.xAccessor(d)).toDate());
        const yTikTok = (d) => yScaleViews(dataTikTok.accessors.yAccessor(d));

        return (
            <svg width={width} height={height}>
                <defs>
                    <clipPath id="date-range-clip-sparkline">
                        <rect
                            x={0}
                            y={0}
                            width={Math.max(width, 0)}
                            height={Math.max(height, 0)}
                            fill="transparent"
                        />
                    </clipPath>
                </defs>
                <Group clipPath="url(#date-range-clip-sparkline)">
                    {dataGoogle.formattedData[0] ? (
                        <>
                            <LinePath
                                x={xGoogle}
                                y={yGoogle}
                                data={dataGoogle.formattedData[0].historical}
                                css={`
                                    stroke: ${({ theme }) => theme.google_blue};
                                    stroke-width: ${strokeWidth}px;
                                    stroke-linejoin: round;
                                `}
                            />
                            <LinePath
                                x={xGoogle}
                                y={yGoogle}
                                data={dataGoogle.formattedData[0].prediction}
                                css={`
                                    stroke: ${({ theme }) => theme.google_blue};
                                    stroke-width: ${strokeWidth}px;
                                    stroke-linejoin: round;
                                `}
                            />
                        </>
                    ) : null}
                    {dataTikTok.formattedData[0] ? (
                        <LinePath
                            x={xTikTok}
                            y={yTikTok}
                            data={dataTikTok.formattedData[0].historical}
                            css={`
                                stroke: ${({ theme }) => theme.tiktok_pink};
                                stroke-width: ${strokeWidth}px;
                                stroke-linejoin: round;
                            `}
                        />
                    ) : null}
                </Group>
            </svg>
        );
    },
);

export default GoogleTikTokSparkline;
